import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Button,
  Divider,
  Card,
  CardContent,
  Avatar,
  Stack,
  useTheme,
  useMediaQuery,
  Fade,
  Grow,
  alpha,
  Skeleton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress
} from '@mui/material';
import {
  Event as EventIcon,
  CalendarToday as CalendarIcon,
  Groups as GroupsIcon,
  Person as PersonIcon,
  Mic as MicIcon,
  TrendingUp as TrendingIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  PhotoCamera as PhotoCameraIcon,
  Public as PublicIcon,
  School as SchoolIcon,
  Assessment as AssessmentIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  Close as CloseIcon,
  Language as LanguageIcon,
  EmojiEvents as TrophyIcon,
  BarChart as ChartIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';

function CafeStatistiqueRGPH_RGAE() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  // Couleurs atténuées
  const primaryColor = '#D32F2F'; // Rouge plus doux
  const secondaryColor = '#2E7D32'; // Vert plus doux
  const tertiaryColor = '#f5f5f5';
  const white = '#ffffff';
  const accentColor = '#2E7D32'; // Vert doux
  const textSecondary = '#666666';
  const neutralGray = '#757575'; // Gris neutre pour les titres

  const [selectedImage, setSelectedImage] = useState(null);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState({});

  // Fonction pour obtenir le chemin des images
  const getImagePath = (filename) => {
    if (!filename) return '';
    
    // Vérifier si c'est déjà une URL complète (fallback Unsplash)
    if (filename.startsWith('http')) {
      return filename;
    }
    
    // Solution 1: Chemin depuis la racine public
    // Si vos images sont dans /public/assets/msb/
    return `/assets/msb/${filename}`;
  };

  // Fonction de secours si les images locales ne fonctionnent pas
  const getFallbackImage = (type = 'event') => {
    const fallbacks = {
      event: "https://images.unsplash.com/photo-1545235617-9465d2a55698?w=800&auto=format&fit=crop",
      portrait: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&auto=format&fit=crop",
      group: "https://images.unsplash.com/photo-1511578314322-379afb476865?w=800&auto=format&fit=crop",
      audience: "https://images.unsplash.com/photo-1545235617-9465d2a55698?w=800&auto=format&fit=crop",
      discussion: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&auto=format&fit=crop"
    };
    return fallbacks[type] || fallbacks.event;
  };

  // Données principales
  const eventData = {
    date: "18 Novembre 2025",
    title: "CAFÉ STATISTIQUE: LA MUTUALISATION DU RGPHAE, UN SUCCÈS POUR LE BURUNDI",
    theme: "Couplage du Recensement Général de la Population et de l'Habitat (RGPH) avec le Recensement Général de l'Agriculture et de l'Élevage (RGAE)",
    sousTheme: "Quelles leçons à tirer de l'expérience du Burundi avec le RGPHAE, édition 2024 ?",
    lieu: "Donatus Conference Center, Bujumbura",
    contexte: "14ème Mois de la Statistique & 33ème Journée Africaine de la Statistique",
    organisateurs: ["Bureau Central du Recensement (BCR)", "Institut National de la Statistique au Burundi (INSBU)"],
    partenaires: ["FNUAP au Burundi", "Ambassade des Pays-Bas au Burundi"],
    objectifs: [
      "Partager les expériences du couplage RGPH-RGAE",
      "Présenter les avantages de la mutualisation",
      "Échanger sur les bonnes pratiques",
      "Valoriser l'innovation statistique"
    ]
  };

  // Intervenants avec photos et paramètres d'affichage
  const intervenants = [
    {
      nom: "M. Nicolas NDAYISHIMIYE",
      titre: "Directeur Général de l'INSBU & Président du BCR",
      role: "Discours d'ouverture et présentation des enjeux du RGPHAE",
      photo: "cafe_statistiquedg.jpg",
      description: "A souligné l'importance de la mutualisation pour optimiser les ressources",
      ordre: 1,
      objectFit: 'contain',
      fallbackType: 'portrait'
    },
    {
      nom: "Dr. KACOU KONAN",
      titre: "Coordinateur des Programmes au FNUAP Burundi",
      role: "Discours sur les technologies innovantes dans les recensements",
      photo: "Cafe_Statistique_18-11-2025_DCC-cordinateurFNUAPB.jpg",
      description: "A mis en avant les avantages des nouvelles technologies pour la qualité des données",
      ordre: 2,
      objectFit: 'contain',
      fallbackType: 'portrait'
    },
    {
      nom: "Directeur Technique",
      titre: "Directeur Technique au Bureau Central du Recensement",
      role: "Présentation technique détaillée du processus RGPHAE",
      photo: "Cafe_Statistique_18-11-2025_DCC-DT.jpg",
      description: "A présenté les aspects techniques et les défis surmontés",
      ordre: 3,
      objectFit: 'contain',
      fallbackType: 'portrait'
    }
  ];

  // Points clés
  const pointsCles = [
    "Mutualisation réussie du RGPH et RGAE en une seule opération",
    "Réduction significative des coûts opérationnels (jusqu'à 40%)",
    "Amélioration notable de la qualité et cohérence des données",
    "Intégration complète du géo-référencement",
    "Respect strict des normes internationales FAO et ONU",
    "Utilisation des technologies innovantes de collecte"
  ];

  // Avantages du couplage
  const avantages = [
    {
      icon: <TrophyIcon />,
      titre: "Optimisation des ressources",
      description: "Utilisation commune des équipes, matériels et infrastructures",
      color: primaryColor
    },
    {
      icon: <ChartIcon />,
      titre: "Cohérence des données",
      description: "Intégration parfaite des données démographiques et agricoles",
      color: secondaryColor
    },
    {
      icon: <CalendarIcon />,
      titre: "Gain de temps",
      description: "Réduction du temps de collecte et de traitement de 30%",
      color: '#1976d2'
    },
    {
      icon: <CheckCircleIcon />,
      titre: "Qualité améliorée",
      description: "Limitation des erreurs et meilleure assurance qualité",
      color: '#388e3c'
    }
  ];

  // Galerie photos avec paramètres d'affichage spécifiques
  const photosEvenement = [
    {
      id: 1,
      src: "Cafe_Statistique_18-11-2025_DCC-photodefamille.jpg",
      alt: "Photo de famille des participants au Café Statistique",
      titre: "Photo de Famille Officielle",
      description: "Participants, organisateurs et partenaires après l'événement",
      category: "official",
      objectFit: 'contain',
      fallbackType: 'group',
      containerHeight: 500
    },
    {
      id: 2,
      src: "Cafe_Statistique_18-11-2025_dg-3.jpg",
      alt: "M. Nicolas NDAYISHIMIYE, DG de l'INSBU, pendant son discours",
      titre: "Discours du DG INSBU",
      description: "Mot de bienvenue et présentation des enjeux",
      category: "speech",
      objectFit: 'contain',
      fallbackType: 'portrait',
      containerHeight: 400
    },
    {
      id: 3,
      src: "Cafe_Statistique_18-11-2025_DCC-cordinateurFNUAPB.jpg",
      alt: "Dr. KACOU KONAN, Coordinateur FNUAP, pendant son intervention",
      titre: "Intervention du FNUAP",
      description: "Discours sur l'innovation technologique",
      category: "speech",
      objectFit: 'contain',
      fallbackType: 'portrait',
      containerHeight: 400
    },
    {
      id: 4,
      src: "Cafe_Statistique_18-11-2025_DCC-public.jpg",
      alt: "Public attentif lors des présentations du Café Statistique",
      titre: "Audience Attentive",
      description: "Participants suivant attentivement les échanges",
      category: "audience",
      objectFit: 'contain',
      fallbackType: 'audience',
      containerHeight: 300
    },
    {
      id: 5,
      src: "Cafe_Statistique_18-11-2025_DCC_new-photopublic.jpg",
      alt: "Nouvelle photo du public participant aux discussions",
      titre: "Échanges et Discussions",
      description: "Moments d'échange entre participants",
      category: "discussion",
      objectFit: 'contain',
      fallbackType: 'discussion',
      containerHeight: 250
    },
     {
      id: 6,
      src: "Cafe_Statistique_18-11-2025_DCC-intervation.jpg",
      alt: "Nouvelle photo du public participant aux discussions",
      titre: "Intervention d'une participante",
      description: "Intervention d'une participante",
      category: "discussion",
      objectFit: 'contain',
      fallbackType: 'discussion',
      containerHeight: 250
    }
  ];

  // Composant Image Optimisé
  const OptimizedImage = ({ 
    src, 
    alt, 
    height = 300, 
    width = '100%', 
    priority = false, 
    onClick, 
    showPreview = true,
    objectFit = 'contain',
    isAvatar = false,
    fallbackType = 'event'
  }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [imageUrl, setImageUrl] = useState('');

    useEffect(() => {
      // Obtenir l'URL de l'image
      const url = getImagePath(src);
      setImageUrl(url);
      
      // Préchargement si priorité
      if (priority && url) {
        const img = new Image();
        img.src = url;
        img.onload = () => {
          setIsLoading(false);
        };
        img.onerror = () => {
          console.error(`Erreur de chargement de l'image: ${url}`);
          setHasError(true);
          setIsLoading(false);
          // Utiliser l'image de secours
          setImageUrl(getFallbackImage(fallbackType));
        };
      }
    }, [src, priority, fallbackType]);

    const handleImageLoad = () => {
      setIsLoading(false);
    };

    const handleImageError = () => {
      console.error(`Erreur de chargement de l'image: ${src}`);
      setHasError(true);
      setIsLoading(false);
      setImageUrl(getFallbackImage(fallbackType));
    };

    const handleClick = () => {
      if (onClick && !hasError && !isLoading) {
        onClick();
      }
    };

    const optimalObjectFit = isAvatar ? 'contain' : objectFit;

    if (hasError && !imageUrl.includes('unsplash')) {
      return (
        <Box 
          sx={{ 
            height: height, 
            width: width, 
            bgcolor: '#f8f9fa',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
            border: '1px dashed #dee2e6',
            cursor: 'default'
          }}
        >
          <ErrorIcon sx={{ fontSize: 48, color: '#adb5bd', mb: 1.5 }} />
          <Typography variant="caption" sx={{ color: '#6c757d', textAlign: 'center', px: 2 }}>
            Image non disponible
          </Typography>
          <Typography variant="caption" sx={{ color: '#adb5bd', fontSize: '0.7rem', mt: 0.5 }}>
            {alt}
          </Typography>
        </Box>
      );
    }

    return (
      <Box 
        sx={{ 
          position: 'relative', 
          height: height, 
          width: width,
          overflow: 'hidden',
          bgcolor: isLoading ? '#f8f9fa' : 'transparent',
          cursor: (showPreview && !isLoading) ? 'pointer' : 'default',
          borderRadius: 1,
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: !isLoading && showPreview ? 'scale(1.02)' : 'none',
            boxShadow: !isLoading && showPreview ? '0 8px 25px rgba(0,0,0,0.15)' : 'none'
          }
        }}
        onClick={handleClick}
      >
        {isLoading && (
          <Skeleton 
            variant="rectangular" 
            width="100%" 
            height={height}
            animation="wave"
            sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              zIndex: 1,
              borderRadius: 1
            }}
          />
        )}
        
        <Box
          component="img"
          src={imageUrl || getFallbackImage(fallbackType)}
          alt={alt}
          onLoad={handleImageLoad}
          onError={handleImageError}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: optimalObjectFit,
            opacity: isLoading ? 0 : 1,
            transition: 'opacity 0.3s ease',
            display: 'block',
            borderRadius: 1,
            imageRendering: 'auto'
          }}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
        />
        
        {showPreview && !isLoading && !hasError && !isAvatar && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 0.3s ease',
              '&:hover': {
                opacity: 1
              }
            }}
          >
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.95)',
              borderRadius: '50%',
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              transform: 'scale(1.1)',
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'scale(1.2)'
              }
            }}>
              <ZoomInIcon sx={{ color: primaryColor, fontSize: 28 }} />
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  // Effet de chargement initial
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  // Fonctions utilitaires
  const handleImageClick = (photo) => {
    setSelectedImage(photo);
    setZoomOpen(true);
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: eventData.title,
        text: eventData.theme,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Lien copié dans le presse-papier !');
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: tertiaryColor,
        flexDirection: 'column',
        gap: 3
      }}>
        <CircularProgress sx={{ color: primaryColor, width: '60px !important', height: '60px !important' }} />
        <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 'bold' }}>
          Chargement de l'événement...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: tertiaryColor,
      background: `linear-gradient(135deg, ${tertiaryColor} 0%, ${alpha(primaryColor, 0.02)} 100%)`
    }}>
      <NavigationBar />
      
      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
        {/* Header avec navigation */}
        <Fade in={true} timeout={500}>
          <Box sx={{ mb: 4 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Button
                onClick={() => handleNavigation('/actualitesEtEvenements')}
                startIcon={<ArrowBackIcon />}
                variant="outlined"
                sx={{ 
                  borderColor: primaryColor,
                  color: primaryColor,
                  fontWeight: 'bold',
                  '&:hover': {
                    bgcolor: alpha(primaryColor, 0.1),
                    borderColor: primaryColor
                  }
                }}
              >
                Retour aux actualités
              </Button>
              <IconButton 
                onClick={handleShare}
                sx={{ 
                  color: secondaryColor,
                  bgcolor: alpha(secondaryColor, 0.1),
                  '&:hover': {
                    bgcolor: alpha(secondaryColor, 0.2)
                  }
                }}
              >
                <ShareIcon />
              </IconButton>
            </Stack>
            
            <Divider sx={{ 
              borderColor: alpha('#000', 0.1),
              borderWidth: 1 
            }} />
          </Box>
        </Fade>

        {/* En-tête Principal */}
        <Fade in={true} timeout={800}>
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 3, sm: 4 }, 
              mb: 4, 
              bgcolor: white,
              background: `linear-gradient(135deg, ${white} 0%, ${alpha(primaryColor, 0.02)} 100%)`,
              borderLeft: `4px solid ${primaryColor}`,
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Chip 
              icon={<CalendarIcon />}
              label={eventData.date}
              sx={{ 
                bgcolor: alpha(primaryColor, 0.9), 
                color: 'white', 
                fontWeight: 'bold',
                mb: 2,
                fontSize: '0.85rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }} 
            />
            
            {/* TITRE PRINCIPAL RÉDUIT */}
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                color: neutralGray,
                mb: 2,
                lineHeight: 1.3,
                fontSize: isMobile ? '1.5rem' : '2.0rem'
              }}
            >
              {eventData.title}
            </Typography>
            
            <Box sx={{ 
              bgcolor: alpha(secondaryColor, 0.08), 
              p: 2.5, 
              borderRadius: 2,
              mb: 3,
              borderLeft: `3px solid ${secondaryColor}`
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: secondaryColor,
                  fontWeight: 600,
                  fontStyle: 'italic',
                  lineHeight: 1.4,
                  fontSize: '1.1rem'
                }}
              >
                {eventData.theme}
              </Typography>
            </Box>
            
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: textSecondary,
                mb: 3,
                fontStyle: 'italic',
                fontSize: '1rem',
                lineHeight: 1.6
              }}
            >
              {eventData.sousTheme}
            </Typography>
            
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3, gap: 1 }}>
              <Chip 
                icon={<EventIcon />}
                label={eventData.contexte}
                size="small"
                sx={{ 
                  borderColor: alpha(primaryColor, 0.3), 
                  color: primaryColor,
                  fontWeight: 600,
                  bgcolor: alpha(primaryColor, 0.05)
                }}
                variant="outlined"
              />
              <Chip 
                icon={<PublicIcon />}
                label={eventData.lieu}
                size="small"
                sx={{ 
                  borderColor: alpha(secondaryColor, 0.3), 
                  color: secondaryColor,
                  fontWeight: 600,
                  bgcolor: alpha(secondaryColor, 0.05)
                }}
                variant="outlined"
              />
            </Stack>
            
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <GroupsIcon sx={{ mr: 1, color: primaryColor, fontSize: 16 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: textSecondary }}>
                    Organisateurs:
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {eventData.organisateurs.map((org, index) => (
                    <Chip 
                      key={index} 
                      label={org} 
                      size="small" 
                      variant="outlined" 
                      sx={{ 
                        borderColor: alpha(primaryColor, 0.3), 
                        color: primaryColor,
                        fontWeight: 600
                      }}
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrophyIcon sx={{ mr: 1, color: secondaryColor, fontSize: 16 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: textSecondary }}>
                    Partenaires:
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {eventData.partenaires.map((part, index) => (
                    <Chip 
                      key={index} 
                      label={part} 
                      size="small" 
                      sx={{ 
                        bgcolor: alpha(secondaryColor, 0.08), 
                        color: secondaryColor,
                        fontWeight: 600
                      }} 
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Fade>

        {/* Objectifs de l'événement avec image */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={12} md={8}>
            <Grow in={true} timeout={1000}>
              <Paper sx={{ 
                p: 3, 
                height: '100%', 
                bgcolor: white, 
                borderRadius: 3,
                boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                border: `1px solid ${alpha('#000', 0.08)}`
              }}>
                {/* TITRE DE SECTION RÉDUIT */}
                <Typography variant="h5" gutterBottom sx={{ color: neutralGray, fontWeight: 700, mb: 3, fontSize: '1.5rem' }}>
                  <EventIcon sx={{ mr: 2, verticalAlign: 'middle', fontSize: '1.3rem' }} />
                  Contexte et Objectifs
                </Typography>
                
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, mb: 3, color: textSecondary, fontSize: '1rem' }}>
                  À l'occasion du 14ème mois de la statistique et de la 33ème Journée Africaine de la Statistique (JAS) 2025, 
                  le Bureau Central du Recensement (BCR) en collaboration avec l'Institut National de la Statistique au Burundi (INSBU), 
                  avec l'appui technique et financier du FNUAP au Burundi et de l'Ambassade des Pays-Bas au Burundi, 
                  ont organisé un café statistique sous le thème de couplage du Recensement Général de la Population et de l'Habitat 
                  avec le Recensement Général de l'Agriculture et de l'Élevage.
                </Typography>
                
                <Typography variant="body1" sx={{ lineHeight: 1.7, color: textSecondary, fontSize: '1rem' }}>
                  Cet événement avait pour objectif de partager les expériences et les leçons apprises de la mutualisation 
                  réussie des deux opérations statistiques majeures, positionnant le Burundi comme un pays modèle en matière 
                  d'innovation statistique.
                </Typography>
              </Paper>
            </Grow>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Grow in={true} timeout={1200}>
              <Box sx={{ 
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: `1px solid ${alpha('#000', 0.1)}`
              }}>
                <OptimizedImage 
                  src={photosEvenement[3].src}
                  alt={photosEvenement[3].alt}
                  height={380}
                  priority={true}
                  onClick={() => handleImageClick(photosEvenement[3])}
                  objectFit={photosEvenement[3].objectFit}
                  fallbackType={photosEvenement[3].fallbackType}
                />
                <Box sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  bgcolor: 'rgba(0,0,0,0.75)',
                  p: 1.5,
                  textAlign: 'center'
                }}>
                  <Typography variant="caption" sx={{ color: 'white', fontSize: '0.75rem', fontWeight: 'medium' }}>
                    Public participant aux échanges
                  </Typography>
                </Box>
              </Box>
            </Grow>
          </Grid>
        </Grid>

        {/* Section Intervenants */}
        <Box sx={{ mb: 2 }}>
          {/* TITRE DE SECTION RÉDUIT */}
          <Typography 
            variant="h5" 
            sx={{ 
              color: neutralGray, 
              fontWeight: 700, 
              mb: 4,
              textAlign: 'center',
              position: 'relative',
              fontSize: '1.6rem',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '80px',
                height: '2px',
                bgcolor: neutralGray,
                borderRadius: 2
              }
            }}
          >
            <GroupsIcon sx={{ mr: 2, verticalAlign: 'middle', fontSize: '1.4rem' }} />
            Intervenants Principaux
          </Typography>

          <Grid container spacing={1}>
            {intervenants.sort((a, b) => a.ordre - b.ordre).map((intervenant, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Grow in={true} timeout={500 + (index * 200)}>
                  <Card sx={{ 
                    height: '100%', 
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease', 
                    '&:hover': { 
                      transform: 'translateY(-6px)',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                    },
                    borderTop: `3px solid ${alpha(primaryColor, 0.7)}`,
                    display: 'flex',
                    flexDirection: 'column',
                    border: `1px solid ${alpha('#000', 0.08)}`
                  }}>
                    <Box sx={{ p: 2.5, textAlign: 'center', flex: 1 }}>
                      <Box 
                        sx={{ 
                          width: 130, 
                          height: 130, 
                          mb: 2.5, 
                          mx: 'auto',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          border: `3px solid ${secondaryColor}`,
                          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                          bgcolor: '#f5f5f5'
                        }}
                      >
                        <OptimizedImage 
                          src={intervenant.photo}
                          alt={`Photo de ${intervenant.nom}`}
                          height={130}
                          width={130}
                          showPreview={false}
                          priority={index < 2}
                          objectFit={intervenant.objectFit}
                          isAvatar={true}
                          fallbackType={intervenant.fallbackType}
                        />
                      </Box>
                      
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: primaryColor, mb: 1, fontSize: '1.1rem' }}>
                        {intervenant.nom}
                      </Typography>
                      
                      <Typography variant="subtitle2" sx={{ color: secondaryColor, mb: 2, fontWeight: 600, fontSize: '0.9rem' }}>
                        {intervenant.titre}
                      </Typography>
                      
                      <Box sx={{ 
                        bgcolor: alpha(primaryColor, 0.05), 
                        p: 1.5, 
                        borderRadius: 2,
                        mb: 2,
                        borderLeft: `2px solid ${alpha(primaryColor, 0.5)}`
                      }}>
                        <Typography variant="body2" sx={{ color: textSecondary, fontStyle: 'italic', fontSize: '0.875rem' }}>
                          {intervenant.role}
                        </Typography>
                      </Box>
                      
                      <Typography variant="caption" sx={{ color: '#888888', lineHeight: 1.6, fontSize: '0.8rem' }}>
                        {intervenant.description}
                      </Typography>
                    </Box>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Points Clés et Avantages */}
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={6}>
              <Grow in={true} timeout={800}>
                <Paper sx={{ 
                  p: 3, 
                  height: '100%', 
                  bgcolor: white,
                  borderRadius: 3,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                  borderTop: `3px solid ${alpha(primaryColor, 0.7)}`,
                  border: `1px solid ${alpha('#000', 0.08)}`
                }}>
                  {/* TITRE DE SECTION RÉDUIT */}
                  <Typography variant="h5" gutterBottom sx={{ color: neutralGray, fontWeight: 700, mb: 3, fontSize: '1.5rem' }}>
                    <CheckCircleIcon sx={{ mr: 2, verticalAlign: 'middle', fontSize: '1.3rem' }} />
                    Principales Réalisations
                  </Typography>
                  
                  <List disablePadding>
                    {pointsCles.map((point, index) => (
                      <ListItem key={index} sx={{ py: 1.2, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Avatar sx={{ 
                            bgcolor: alpha(primaryColor, 0.9), 
                            width: 26, 
                            height: 26, 
                            fontSize: '0.85rem',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                          }}>
                            {index + 1}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText 
                          primary={point}
                          primaryTypographyProps={{ 
                            fontWeight: 500,
                            color: textSecondary,
                            lineHeight: 1.5,
                            fontSize: '0.95rem'
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grow>
            </Grid>

            <Grid item xs={12} md={6}>
              <Grow in={true} timeout={1000}>
                <Paper sx={{ 
                  p: 3, 
                  height: '100%', 
                  bgcolor: white,
                  borderRadius: 3,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                  borderTop: `3px solid ${alpha(secondaryColor, 0.7)}`,
                  border: `1px solid ${alpha('#000', 0.08)}`
                }}>
                  {/* TITRE DE SECTION RÉDUIT */}
                  <Typography variant="h5" gutterBottom sx={{ color: neutralGray, fontWeight: 700, mb: 3, fontSize: '1.5rem' }}>
                    <TrendingIcon sx={{ mr: 2, verticalAlign: 'middle', fontSize: '1.3rem' }} />
                    Avantages du Couplage
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {avantages.map((avantage, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Box sx={{ 
                          p: 2, 
                          bgcolor: alpha(avantage.color, 0.05), 
                          borderRadius: 2,
                          height: '100%',
                          transition: 'transform 0.3s ease',
                          '&:hover': { transform: 'translateY(-4px)' },
                          border: `1px solid ${alpha(avantage.color, 0.15)}`
                        }}>
                          <Box sx={{ 
                            display: 'inline-flex', 
                            p: 1.2, 
                            mb: 1.5,
                            bgcolor: alpha(avantage.color, 0.1),
                            borderRadius: 2
                          }}>
                            {React.cloneElement(avantage.icon, { sx: { color: avantage.color, fontSize: 22 } })}
                          </Box>
                          <Typography variant="subtitle1" sx={{ color: avantage.color, mb: 1, fontWeight: 600, fontSize: '1rem' }}>
                            {avantage.titre}
                          </Typography>
                          <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.9rem' }}>
                            {avantage.description}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grow>
            </Grid>
          </Grid>
        </Box>

        {/* Contenu Détaillé */}
        <Grid container spacing={4} sx={{ mb: 2 }}>
          <Grid item xs={12} md={12}>
            <Grow in={true} timeout={1000}>
              <Paper sx={{ 
                p: 3, 
                height: '100%', 
                bgcolor: white,
                borderRadius: 3,
                boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                border: `1px solid ${alpha('#000', 0.08)}`
              }}>
                {/* TITRE DE SECTION RÉDUIT */}
                <Typography variant="h5" gutterBottom sx={{ color: neutralGray, fontWeight: 700, mb: 3, fontSize: '1.5rem' }}>
                  <MicIcon sx={{ mr: 2, verticalAlign: 'middle', fontSize: '1.3rem' }} />
                  Présentations et Discussions
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: secondaryColor, fontWeight: 600, mb: 2, fontSize: '1.2rem' }}>
                    Discours d'ouverture du DG INSBU
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: textSecondary, fontSize: '1rem' }}>
                    Dans son discours d'ouverture, Monsieur Nicolas NDAYISHIMIYE, Directeur Général de l'INSBU 
                    et Président du Bureau Central du Recensement, a souligné que le Café Statistique s'inscrivait 
                    dans le cadre de la poursuite des activités programmées au cours du 14ème Mois de la Statistique au Burundi.
                  </Typography>
                  
                  <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: textSecondary, fontSize: '1rem' }}>
                    Il a précisé que cet événement intervenait à point nommé, alors que le Burundi venait d'inscrire 
                    son nom parmi les nations ayant réalisé leur recensement général de la population et de l'habitat, 
                    coïncidant avec le lancement par la FAO de son programme mondial pour les recensements généraux 
                    de l'Agriculture et de l'Élevage.
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ color: secondaryColor, fontWeight: 600, mb: 2, fontSize: '1.2rem' }}>
                    Intervention du Coordinateur FNUAP
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: textSecondary, fontSize: '1rem' }}>
                    Le Docteur KACOU KONAN, Coordinateur des Programmes au FNUAP Burundi, a mis en avant 
                    l'importance des nouvelles technologies de l'information et de la communication dans 
                    la réalisation des opérations statistiques modernes.
                  </Typography>
                  
                  <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: textSecondary, fontSize: '1rem' }}>
                    Il a souligné que le couplage RGPH-RGAE permettait non seulement une réduction significative 
                    des coûts, mais aussi une limitation des erreurs d'écriture, une amélioration de l'assurance 
                    qualité, une réduction des délais de traitement des données, et une amélioration globale 
                    de la qualité des analyses.
                  </Typography>
                </Box>
              </Paper>
            </Grow>
          </Grid>
        </Grid>

        {/* Table Ronde et Conclusion */}
        <Paper sx={{ 
          p: 3, 
          mb: 6, 
          bgcolor: white,
          borderRadius: 3,
          boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
          borderLeft: `4px solid ${alpha(secondaryColor, 0.8)}`,
          border: `1px solid ${alpha('#000', 0.08)}`
        }}>
          {/* TITRE DE SECTION RÉDUIT */}
          <Typography variant="h5" gutterBottom sx={{ color: neutralGray, fontWeight: 700, mb: 3, fontSize: '1.5rem' }}>
            <GroupsIcon sx={{ mr: 2, verticalAlign: 'middle', fontSize: '1.3rem' }} />
            Table Ronde et Conclusion
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, mb: 3, color: textSecondary, fontSize: '1rem' }}>
            Après les présentations techniques détaillées par le Directeur Technique du BCR sur le processus 
            du RGPHAE, une table ronde enrichissante a réuni les principaux acteurs. Les discussions ont 
            confirmé que le Burundi s'est distingué comme un pays modèle en mutualisant avec succès deux 
            opérations statistiques majeures.
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, mb: 3, color: textSecondary, fontSize: '1rem' }}>
            Les panélistes, incluant le Directeur Général de l'INSBU et les conseillers techniques du BCR, 
            ont unanimement salué le respect strict des normes internationales établies par la FAO et les 
            Nations Unies, tout en intégrant les approches modulaires innovantes recommandées pour les 
            recensements agricoles.
          </Typography>
          
          <Box sx={{ 
            p: 3, 
            bgcolor: alpha(secondaryColor, 0.04), 
            borderRadius: 3,
            border: `1px solid ${alpha(secondaryColor, 0.15)}`
          }}>
            <Typography variant="h6" sx={{ color: secondaryColor, fontWeight: 600, mb: 2, fontSize: '1.2rem' }}>
              Conclusions Principales
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: textSecondary, fontSize: '1rem' }}>
              Le Café Statistique a démontré que la mutualisation RGPHAE représente un succès remarquable 
              pour le Burundi, établissant un nouveau standard d'excellence en matière d'innovation statistique 
              et positionnant le pays comme une référence régionale en Afrique. Cette approche innovante 
              servira de modèle pour les futurs recensements dans la région.
            </Typography>
          </Box>
        </Paper>

        {/* Galerie Photos */}
        <Box sx={{ mb: 6 }}>
          {/* TITRE DE SECTION RÉDUIT */}
          <Typography 
            variant="h5" 
            sx={{ 
              color: neutralGray, 
              fontWeight: 700, 
              mb: 4,
              textAlign: 'center',
              position: 'relative',
              fontSize: '1.6rem',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '80px',
                height: '2px',
                bgcolor: neutralGray,
                borderRadius: 2
              }
            }}
          >
            <PhotoCameraIcon sx={{ mr: 2, verticalAlign: 'middle', fontSize: '1.4rem' }} />
            Galerie Photo de l'Événement
          </Typography>

          <Grid container spacing={2}>
            {photosEvenement.map((photo, index) => (
              <Grid item xs={12} sm={6} md={4} key={photo.id}>
                <Grow in={true} timeout={300 + (index * 200)}>
                  <Card sx={{ 
                    height: '100%', 
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease', 
                    '&:hover': { 
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 32px rgba(0,0,0,0.1)'
                    },
                    overflow: 'hidden',
                    border: `1px solid ${alpha('#000', 0.08)}`
                  }}>
                    <Box sx={{ position: 'relative', height: photo.containerHeight || 250 }}>
                      <OptimizedImage 
                        src={photo.src}
                        alt={photo.alt}
                        height={photo.containerHeight || 250}
                        priority={index < 2}
                        onClick={() => handleImageClick(photo)}
                        objectFit={photo.objectFit}
                        fallbackType={photo.fallbackType}
                      />
                      <Box sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        bgcolor: 'rgba(255,255,255,0.9)',
                        borderRadius: '50%',
                        width: 30,
                        height: 30,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                      }}>
                        <PhotoCameraIcon sx={{ fontSize: 16, color: primaryColor }} />
                      </Box>
                    </Box>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: primaryColor, fontSize: '0.95rem' }}>
                        {photo.titre}
                      </Typography>
                      <Typography variant="caption" sx={{ color: textSecondary, lineHeight: 1.5, fontSize: '0.8rem' }}>
                        {photo.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Navigation */}
        <Paper 
          sx={{ 
            p: 3, 
            mb: 4,
            bgcolor: white,
            borderRadius: 3,
            border: `1px solid ${alpha(primaryColor, 0.15)}`,
            textAlign: 'center',
            boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
            background: `linear-gradient(135deg, ${white} 0%, ${alpha(secondaryColor, 0.02)} 100%)`
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ color: neutralGray, fontWeight: 700, mb: 3, fontSize: '1.2rem' }}>
            Découvrez d'autres événements
          </Typography>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              onClick={() => handleNavigation('/clotureMoisStatistique2025')}
              variant="contained"
              startIcon={<EventIcon />}
              sx={{ 
                bgcolor: neutralGray,
                '&:hover': { bgcolor: '#616161' },
                px: 3,
                py: 1.1,
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            >
              Cérémonie de Clôture
            </Button>
            
            <Button
              onClick={() => handleNavigation('/lancementMoisStatistique2025')}
              variant="contained"
              startIcon={<CalendarIcon />}
              sx={{ 
                bgcolor: alpha(primaryColor, 0.9),
                '&:hover': { bgcolor: primaryColor },
                px: 3,
                py: 1.1,
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            >
              Lancement MSB 2025
            </Button>
            
            <Button
              onClick={() => handleNavigation('/actualitesEtEvenements')}
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              sx={{ 
                borderColor: alpha(neutralGray, 0.4),
                color: neutralGray,
                '&:hover': {
                  borderColor: neutralGray,
                  bgcolor: alpha(neutralGray, 0.05)
                },
                px: 3,
                py: 1.1,
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            >
              Toutes les actualités
            </Button>
          </Stack>
        </Paper>
        
        {/* Signature */}
        <Paper sx={{ 
          p: 3, 
          mb: 4, 
          bgcolor: white, 
          borderRadius: 3,
          border: `1px solid ${alpha(accentColor, 0.15)}`, 
          textAlign: 'center',
          boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
        }}>
          <Typography variant="body1" sx={{ color: textSecondary, fontWeight: 500, fontSize: '0.95rem' }}>
            Présenté par Ir. Léandre NIMPAGARITSE, Informaticien – Agent de Publication, Diffusion et Communication à l'INSBU.
          </Typography>
        </Paper>
      </Container>
      
      {/* Modal pour zoom sur image */}
      <Dialog
        open={zoomOpen}
        onClose={() => setZoomOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ 
          position: 'relative',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'black'
        }}>
          <IconButton 
            onClick={() => setZoomOpen(false)}
            sx={{ 
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: 'rgba(255,255,255,0.9)',
              '&:hover': {
                bgcolor: 'white'
              },
              zIndex: 1000
            }}
          >
            <CloseIcon />
          </IconButton>
          
          {selectedImage && (
            <OptimizedImage 
              src={selectedImage.src}
              alt={selectedImage.alt}
              height="auto"
              width="100%"
              showPreview={false}
              onClick={() => {}}
              objectFit="contain"
              fallbackType={selectedImage.fallbackType}
            />
          )}
          
          <Box sx={{ 
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: 'rgba(0,0,0,0.8)',
            p: 2,
            color: 'white'
          }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
              {selectedImage?.titre}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              {selectedImage?.description}
            </Typography>
          </Box>
        </Box>
      </Dialog>
      
      <Footer />
    </Box>
  );
}

export default CafeStatistiqueRGPH_RGAE;